import { Injectable, Inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { NavigationExtras, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { LocalStorageService } from '@shared/services/local-storage/local-storage.service';
import { AppState } from '@store/state';
import * as fullscreenLoaderActions from '@store/fullscreen-loader/fullscreen-loader.actions';
import * as errorModalActions from '@store/error-modal/error-modal.actions';
import { ErrorModalModel } from '@store/error-modal/error-modal.model';
import rg4js from 'raygun4js';
import { HttpErrorCodeEnum } from '@enums/http-error-code.enum';
import { ApiErrorCodeEnum } from '@enums/api-error-code.enum ';
import { UrlPathnameEnum } from '@enums/url-pathname.enum';
import { ContentFieldsEnum } from '@enums/content-fields.enum';
import { selectErrorMessagesList, selectErrorModalVisibility } from '@store/error-modal/error-modal.selectors';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private terminateSubscription = new Subject();

  constructor(
    @Inject(Router) private _router: Router,
    @Inject(Store) private _store: Store<AppState>,
    @Inject(LocalStorageService) private _localStorageService: LocalStorageService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: HttpEvent<any>) => {
        if (err instanceof HttpErrorResponse) {
          this.raygunLogging(err);
          this._store.dispatch(fullscreenLoaderActions.setVisibility({ isVisible: false }));
          const message = err?.error?.Reason || null;
          const navigationExtras = message ? { queryParams: { message } } : {};
          switch (err.status) {
            case ApiErrorCodeEnum.Undefined:
              this._router.navigate([HttpErrorCodeEnum.Unauthorized], navigationExtras);
              break;
            case HttpErrorCodeEnum.Unauthorized:
              this._router.navigate([HttpErrorCodeEnum.Unauthorized], navigationExtras);
              break;
            case HttpErrorCodeEnum.Forbidden:
              this._router.navigate([HttpErrorCodeEnum.Forbidden], navigationExtras);
              break;
            case HttpErrorCodeEnum.NotFound:
              this._router.navigate([HttpErrorCodeEnum.NotFound], navigationExtras);
              break;
            case HttpErrorCodeEnum.BadRequest:
              this.processBadRequest(err, navigationExtras);
              break;
            default:
              this.showErrorModal(err);
              return of(err);
          }
        }
      })
    );
  }

  public showErrorModal(err: HttpErrorResponse): void {
    const message = err?.error?.message ?? '';
    let code = err?.error?.textCode;
    if (!code) {
      code = err?.error?.code?.toString();
    }

    this.retrieveContentfulErrorData(code)
      .pipe(takeUntil(this.terminateSubscription))
      .subscribe((response) => {
        let isModalVisible = null;
        this._store.select(selectErrorModalVisibility).subscribe((isVisible) => {
          isModalVisible = isVisible;
        });
        if (response.error) {
          this._store.dispatch(
            errorModalActions.setData({
              data: {
                isVisible: true,
                errorStatus: err.status,
                errorCode: code,
                defaultContent: response.errorMessagesList,
                message,
              } as ErrorModalModel,
            })
          );
        } else {
          this.retrieveContentfulErrorData(ContentFieldsEnum.Generic).subscribe((genericErrorResponse) => {
            this._store.dispatch(
              errorModalActions.setData({
                data: {
                  isVisible: true,
                  errorStatus: err.status,
                  errorCode: code,
                  defaultContent: genericErrorResponse.errorMessagesList,
                  message,
                } as ErrorModalModel,
              })
            );
          });
        }
      });

    this.emitTerminateSubscription();
  }
  private emitTerminateSubscription() {
    this.terminateSubscription.next();
  }

  private retrieveContentfulErrorData(code: string): Observable<any> {
    return this._store.select(selectErrorMessagesList).pipe(
      map((errors) => {
        if (!errors) {
          return undefined;
        }
        return {
          errorMessagesList: errors,
          error: errors.find((error) => error.code === code),
        };
      })
    );
  }

  private isSessionExpired(errorCode: number): boolean {
    return Boolean(
      errorCode &&
        (errorCode === ApiErrorCodeEnum.NoDataFound ||
          errorCode === ApiErrorCodeEnum.InvalidMethodCall ||
          errorCode === ApiErrorCodeEnum.ValidationFailed ||
          errorCode === ApiErrorCodeEnum.AuthorizationFailed ||
          errorCode === ApiErrorCodeEnum.AuthenticationFailed ||
          errorCode === ApiErrorCodeEnum.ContextFailed ||
          errorCode === ApiErrorCodeEnum.ClientNotAllowedForUser ||
          errorCode === ApiErrorCodeEnum.InvalidToken)
    );
  }

  private raygunLogging(errorResponse: HttpErrorResponse | Error): void {
    if (errorResponse instanceof HttpErrorResponse) {
      rg4js('send', {
        error: this.errorParse(errorResponse),
        tags: ['handled_error', this.errorParse(errorResponse), this.getErrorStatusOrName(errorResponse)],
      });
    } else {
      rg4js('send', {
        error: errorResponse.message,
        tags: [this.getErrorStatusOrName(errorResponse)],
      });
    }
  }

  private errorParse(errorResponse: HttpErrorResponse) {
    return `Error Code: ${errorResponse?.error?.ErrorCode ? errorResponse?.error?.ErrorCode : errorResponse.error?.Message}`;
  }

  private getErrorStatusOrName(errorResponse: HttpErrorResponse | Error) {
    if (errorResponse instanceof HttpErrorResponse) {
      return `Error status: ${errorResponse?.status}`;
    } else {
      return `Error message: ${errorResponse?.name}`;
    }
  }

  private processBadRequest(err: HttpErrorResponse, navigationExtras: NavigationExtras) {
    if (this.isSessionExpired(err?.error?.errorCode)) {
      localStorage.clear();
      this._router.navigate([UrlPathnameEnum.Login], { queryParams: { checkAuthorized: 'true' } });
    } else {
      this._localStorageService.getUserSessionToken()
        ? this.showErrorModal(err)
        : this._router.navigate([HttpErrorCodeEnum.Unauthorized], navigationExtras);
    }
  }
}
